import {fileDownload, request} from '../util/request';
import {GLOBAL_CONNECTION_API, GLOBAL_KNOWLEDGE_SEARCH_API, GLOBAL_RULES_PROBLEM_API} from '../config/api';
import {ModuleEnum} from "../config/constant";
import {GLOBAL_KNOWLEDGE_BASE_ORIGIN} from '../config/origin';
import {encodeGetParams, ObjTest} from 'jzfz-web-utils';


interface ArticleParams {
    appType: ModuleEnum;    // 知识类型
    pkid: string;   // 知识主键
    data?: any; // 知识详情信息; 如果有数据，则不会再通过接口获取知识详情，而是直接从该对象中获取相关信息
    params?: Record<string, string | number>;    // query参数
}


interface ArticleInfoOptions {
    excludeOrigin?: boolean;    // true: 不包含主机地址 false: 包含主机地址
    needMaterialId?: boolean;   // 是否需要素材id
}

interface DownloadAttachmentOption {
    appType?: ModuleEnum;   // 知识类型
}


/**
 * 格式化路径
 * @param paths
 * @param search
 */
const formatPath = (paths: string[], search: Record<string, string | number>) => {
    let path = paths.filter((path: string) => path).join('/')
    if (!ObjTest.isEmptyObject(search)) {
        path += `?${encodeGetParams(search)}`;
    }
    return path;
}


/**
 * 获取知识信息
 * @param data
 * @param options
 */
export const getArticleInfo = async (data: ArticleParams, options: ArticleInfoOptions = {}): Promise<{url, data, origin}> => {
    const { appType, pkid, data: content, params } = data;
    const { excludeOrigin, needMaterialId } = options;  // excludeOrigin 是否排除主机地址

    let queryParams: any;
    if(needMaterialId) {
        queryParams = {appType, pkId: pkid, needMaterialId};
    }else {
        queryParams = {appType, pkId: pkid};
    }

    let info: any = {};    // 知识详情
    if (content) {
        info = content;
    }else {
        const res = await request('/knowledge/jumparg/get', queryParams, {baseURL: GLOBAL_KNOWLEDGE_SEARCH_API});
        info = res.data;
    }

    const { pkId, major, status, regulationCatalogId,
        catalogType, customerPkId: customerPkid, standardId, catalogId, packageId,
        menuId, mapId, mapType, examCatalogId, appType: moduleType,
        articleCategoryId, schemeCategoryId, schemeId, materialId, projectCode,
        customerType,
    } = info;

    // 文本库跳转需要的参数对象
    const dwgObj = {articleCategoryId, schemeCategoryId, schemeId, knowledgePkId: pkid};
    // 节点库跳转需要的参数对象
    const materialObj = {materialId};
    // 平面库跳转需要的参数对象
    let planeObj: any;
    if(materialId) {
        planeObj = {projectCode, materialId};
    }else {
        planeObj = {projectCode};
    }

    let url = '';
    let search = {};
    // 由于附件需要跳转到上传该附件的知识，所以要取接口返回的appType
    let type = appType === ModuleEnum.attachment ? moduleType : appType;

    switch (type) {
        case ModuleEnum.help:
            // 帮助文档
            url = formatPath(['/help', pkId], params);
            break;
        case ModuleEnum.typical:
            // 典型问题
            search = {
                ...params,
                majorId: major,
                status,
            }
            url = formatPath(['/typicalQuestion', pkId], search);
            break;
        case ModuleEnum.rules:
            // 公司规定问题
            search = {
                ...params,
                catalogType,
                customerPkid,
                customerType,
                majorId: major,
            }
            url = formatPath(['/rulesProblem', regulationCatalogId, pkId], search);
            break;
        case ModuleEnum.standard:
            // 国家规范
            search = {
                ...params,
                majorId: major,
            }
            url = formatPath(['/standard', standardId, catalogId, pkId], search);
            break;
        case ModuleEnum.learn:
            // 学习资料
            search = {
                ...params,
                majorId: major,
            }
            url = formatPath(['/learnData', packageId, pkId], search)
            break;
        case ModuleEnum.standardTask:
            // 标准任务
            search = {
                ...params,
                majorId: major,
            }
            url = formatPath(['/standardTask', menuId, pkId], search);
            break;
        case ModuleEnum.standardMap:
            // 标准图集
            search = {
                ...params,
                majorId: major,
                mapType,
            }
            url = formatPath(['/standardMap', mapId, pkId], search);
            break;
        case ModuleEnum.exam:
            // 题库
            search = {
                ...params,
                majorId: major,
            }
            url = formatPath(['/exam/questionManage', examCatalogId, pkId], search);
            break;
        case ModuleEnum.dwgMaterial:
            // 建筑节点
            url = `/dwgRepository/dwgMaterial?${encodeGetParams(materialObj)}`;
            break;
        case ModuleEnum.dwgPlane:
            // 建筑平面
            url = `/dwgRepository/dwgPlane?${encodeGetParams(planeObj)}`;
            break;
        case ModuleEnum.dwgScheme:
            // 文本库
            url = `/dwgRepository/dwgScheme?${encodeGetParams(dwgObj)}`;
            break;
        case ModuleEnum.dwgLib:
            // 图纸库
            url = `/dwgRepository/dwgPic?${encodeGetParams(dwgObj)}`;
            break;
        case ModuleEnum.dwgElevator:
            // 建筑电梯
            url = `/dwgRepository/dwgElevator?${encodeGetParams(materialObj)}`;
            break;
        case ModuleEnum.dwgMarketing:
            // 技术营销
            url = `/dwgRepository/technicalMarketing?${encodeGetParams(dwgObj)}`;
            break;
        case ModuleEnum.projectSummary:
            // 项目总结
            url = `/dwgRepository/projectSummary?${encodeGetParams(dwgObj)}`;
            break;
        case ModuleEnum.technicalReport:
            // 技术汇报
            url = `/dwgRepository/technicalReport?${encodeGetParams(dwgObj)}`;
            break;
        case ModuleEnum.HVACDwgMaterial:
            // 暖通节点
            url = `/dwgRepository/HVACDwgMaterial?${encodeGetParams(materialObj)}`;
            break;
        case ModuleEnum.electricalMaterial:
            // 电气样图节点
            url = `/dwgRepository/electricalMaterial?${encodeGetParams(materialObj)}`;
            break;
        case ModuleEnum.plumbingMaterial:
            // 给排水样图
            url = `/dwgRepository/plumbingMaterial?${encodeGetParams(materialObj)}`;
            break;
        case ModuleEnum.structNodeMaterial:
            // 结构样图
            url = `/dwgRepository/structNodeMaterial?${encodeGetParams(materialObj)}`;
            break;
    }

    return {
        url: excludeOrigin ? url : `${GLOBAL_KNOWLEDGE_BASE_ORIGIN}/#${url}`,
        data: info,
        origin: GLOBAL_KNOWLEDGE_BASE_ORIGIN,
    };
}


/**
 * 获取知识链接
 * @param appType
 * @param pkid
 * @deprecated 使用函数 getArticleInfo 替代
 */
export const getArticleURL = async (appType: ModuleEnum, pkid: string, params?: string): Promise<string> => {
    // search字符串转对象
    let search = {};
    if (ObjTest.isStr(params) && params) {
        let temp: string[] = params.split('&');
        temp.forEach((v: string) => {
            if (!v) {
                return;
            }
            let index = v.indexOf('=');
            if (index > -1) {
                search[v.substring(0, index)] = decodeURIComponent(v.substring(index + 1));
            }
        })
    }

    const { url } = await getArticleInfo({appType, pkid, params: search});
    return url;
}


/**
 * 下载附件
 * @param attachmentId
 * @param options
 */
export const downloadAttachment = (attachmentId: string, options?: DownloadAttachmentOption): Promise<any> => {
    const {appType} = options || {};
    let params: any = {attachmentId, ...options};
    if(!appType) {
        delete params['appType']
    }

    return fileDownload('/attachment/download', params, {method: 'get', baseURL: GLOBAL_CONNECTION_API});
}


/**
 * 获取附件预览URL
 * @param attachmentId
 */
export const getAttachmentPreviewURL = (attachmentId: string): Promise<any> => {
    return request('/attachment/preview', {attachmentId}, {method: 'get', baseURL: GLOBAL_CONNECTION_API});
}


/**
 * 查询专业集合
 */
export const getMajors = () => {
    return request('/regulation/major/selectMajorList', {}, {baseURL: GLOBAL_RULES_PROBLEM_API});
}


const commonAPI = {
    getArticleInfo,
    getArticleURL,
    downloadAttachment,
    getAttachmentPreviewURL,
    getMajors,
}


export default commonAPI;
